import React, { useEffect, useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent } from '../components/Card';
import { Briefcase, Clock, User, Plus } from 'lucide-react';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/Dialog';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { Label } from '../components/Label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/Select';
import { toast } from 'sonner';
import { mockApi } from '../lib/mockApi';
import { Network, FileText, Megaphone, Activity } from 'lucide-react';
import { RelatedWorkflow } from '../components/dashboard/RelatedWorkflow';
type Task = {
  id: string;
  title: string;
  pic: string;
  deadline: string;
  severity: 'KRITIS' | 'TINGGI' | 'SEDANG';
  status: 'BELUM' | 'PROSES' | 'REVIEW' | 'SELESAI';
};
export function CommandTasking() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    pic: '',
    deadline: '24 jam',
    severity: 'SEDANG' as const
  });
  useEffect(() => {
    const loadData = async () => {
      const data = await mockApi.getTasks();
      setTasks(data as Task[]);
      setLoading(false);
    };
    loadData();
    return mockApi.subscribe(loadData);
  }, []);
  const handleAddTask = async () => {
    if (!newTask.title || !newTask.pic) {
      toast.error('Lengkapi form terlebih dahulu');
      return;
    }
    setIsSubmitting(true);
    try {
      await mockApi.addTask(newTask);
      toast.success('Tasking berhasil dibuat dan dikirim ke PIC');
      setAdding(false);
      setNewTask({
        title: '',
        pic: '',
        deadline: '24 jam',
        severity: 'SEDANG'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleStatusChange = async (id: string, currentStatus: string) => {
    const nextStatus = currentStatus === 'BELUM' ? 'PROSES' : currentStatus === 'PROSES' ? 'REVIEW' : 'SELESAI';
    toast.promise(mockApi.updateTaskStatus(id, nextStatus), {
      loading: 'Memperbarui status...',
      success: `Status ${id} diperbarui menjadi ${nextStatus}`,
      error: 'Gagal memperbarui status'
    });
  };
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6" data-id="element-1397">
      <PageHeader group="Komando & Respons" title="Command Tasking" description="Kanban board untuk distribusi & tracking tugas ke Kementerian/Lembaga. Setiap tasking persist real-time." icon={Briefcase} extraActions={<Button size="sm" onClick={() => setAdding(true)} data-id="element-1399">
            <Plus className="size-4 mr-2" data-id="element-1400" />
            Buat Tugas Baru
          </Button>} data-id="element-1398" />

      {/* Kanban Board: 4 status columns */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-1401">
        {['BELUM', 'PROSES', 'REVIEW', 'SELESAI'].map(status => <div key={status} className="flex flex-col gap-3" data-id="element-1402">
            <div className="flex items-center justify-between pb-2 border-b border-border/50" data-id="element-1403">
              <h3 className="font-heading text-sm font-bold" data-id="element-1404">{status}</h3>
              <Badge variant="outline" className="font-mono text-[10px]" data-id="element-1405">
                {tasks.filter(t => t.status === status).length}
              </Badge>
            </div>

            {loading ? <div className="space-y-3" data-id="element-1406">
                <div className="h-24 bg-muted/20 animate-pulse rounded-lg" data-id="element-1407" />
                <div className="h-24 bg-muted/20 animate-pulse rounded-lg" data-id="element-1408" />
              </div> : <div className="flex flex-col gap-3 min-h-[200px] rounded-lg bg-muted/5 p-2" data-id="element-1409">
                {tasks.filter(t => t.status === status).map(t => <Card key={t.id} className="bg-card border-border/50 shadow-sm hover:border-primary/30 transition cursor-pointer group" data-id="element-1410">
                      <CardContent className="p-3" data-id="element-1411">
                        <div className="flex items-start justify-between mb-2" data-id="element-1412">
                          <Badge variant="outline" className={`font-mono text-[8px] px-1 py-0 h-4 border-none ${t.severity === 'KRITIS' ? 'bg-red-500/10 text-red-500' : t.severity === 'TINGGI' ? 'bg-amber-500/10 text-amber-500' : 'bg-blue-500/10 text-blue-500'}`} data-id="element-1413">
                            {t.severity}
                          </Badge>
                          <span className="font-mono text-[9px] text-muted-foreground group-hover:text-primary transition" data-id="element-1414">
                            {t.id}
                          </span>
                        </div>
                        <p className="text-xs font-bold leading-snug mb-3" data-id="element-1415">
                          {t.title}
                        </p>
                        <div className="flex items-center justify-between text-[10px] text-muted-foreground" data-id="element-1416">
                          <div className="flex items-center gap-1" data-id="element-1417">
                            <User className="size-3" data-id="element-1418" />
                            <span className="truncate max-w-[80px]" data-id="element-1419">
                              {t.pic}
                            </span>
                          </div>
                          <div className="flex items-center gap-1" data-id="element-1420">
                            <Clock className="size-3" data-id="element-1421" />
                            <span className={t.deadline.includes('jam') && parseInt(t.deadline) <= 4 ? 'text-red-500 font-bold' : ''} data-id="element-1422">
                              {t.deadline}
                            </span>
                          </div>
                        </div>
                        {status !== 'SELESAI' && <Button size="xs" variant="outline" className="w-full mt-3 h-6 text-[9px]" onClick={() => handleStatusChange(t.id, t.status)} data-id="element-1423">
                            Pindah ke{' '}
                            {status === 'BELUM' ? 'PROSES' : status === 'PROSES' ? 'REVIEW' : 'SELESAI'}
                          </Button>}
                      </CardContent>
                    </Card>)}
              </div>}
          </div>)}
      </div>
      <Dialog open={adding} onOpenChange={setAdding} data-id="element-1424">
        <DialogContent data-id="element-1425">
          <DialogHeader data-id="element-1426">
            <DialogTitle data-id="element-1427">Tugas Baru</DialogTitle>
            <DialogDescription data-id="element-1428">
              Buat tugas dan tetapkan PIC, deadline, serta tingkat severity.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3" data-id="element-1429">
            <div data-id="element-1430">
              <Label data-id="element-1431">Judul Tugas</Label>
              <Input placeholder="cth: Klarifikasi hoaks XYZ" value={newTask.title} onChange={e => setNewTask({
              ...newTask,
              title: e.target.value
            })} data-id="element-1432" />
            </div>
            <div data-id="element-1433">
              <Label data-id="element-1434">PIC (Kementerian / Lembaga)</Label>
              <Input placeholder="cth: Komdigi - Ditjen Aptika" value={newTask.pic} onChange={e => setNewTask({
              ...newTask,
              pic: e.target.value
            })} data-id="element-1435" />
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-1436">
              <div data-id="element-1437">
                <Label data-id="element-1438">Tingkat Prioritas</Label>
                <Select value={newTask.severity} onValueChange={(v: any) => setNewTask({
                ...newTask,
                severity: v
              })} data-id="element-1439">
                  <SelectTrigger className="mt-1.5" data-id="element-1440">
                    <SelectValue data-id="element-1441" />
                  </SelectTrigger>
                  <SelectContent data-id="element-1442">
                    <SelectItem value="KRITIS" data-id="element-1443">KRITIS</SelectItem>
                    <SelectItem value="TINGGI" data-id="element-1444">TINGGI</SelectItem>
                    <SelectItem value="SEDANG" data-id="element-1445">SEDANG</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div data-id="element-1446">
                <Label data-id="element-1447">Deadline</Label>
                <Input placeholder="cth: 4 jam" value={newTask.deadline} onChange={e => setNewTask({
                ...newTask,
                deadline: e.target.value
              })} data-id="element-1448" />
              </div>
            </div>
            <div data-id="element-1449">
              <Label data-id="element-1450">Catatan</Label>
              <Textarea placeholder="Detail instruksi..." data-id="element-1451" />
            </div>
          </div>
          <DialogFooter data-id="element-1452">
            <Button variant="outline" onClick={() => setAdding(false)} data-id="element-1453">
              Batal
            </Button>
            <Button onClick={handleAddTask} disabled={isSubmitting} data-id="element-1454">
              {isSubmitting ? 'Menyimpan...' : 'Kirim Tasking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <RelatedWorkflow currentStep="Tasking K/L" nextSteps={[{
      to: '/ministry-mapping',
      icon: Network,
      label: 'Pantau Performa K/L',
      desc: 'Lihat kepatuhan & respons kementerian terhadap tasking.'
    }, {
      to: '/daily-brief',
      icon: FileText,
      label: 'Update Daily Brief',
      desc: 'Sertakan tasking aktif di brief Presiden.'
    }, {
      to: '/response-recommender',
      icon: Megaphone,
      label: 'Generate Pesan Resmi',
      desc: 'Siapkan respons publik paralel tasking.'
    }, {
      to: '/crisis-simulation',
      icon: Activity,
      label: 'Simulasi Skenario',
      desc: 'Uji konsekuensi tasking sebelum dieksekusi.'
    }]} data-id="element-1455" />
    </div>;
}